import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';

const ToastContext = createContext(null);

let toastId = 0;

export function ToastProvider({ children }) {
  const [toasts, setToasts] = useState([]);

  const dismiss = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (toast) => {
      const id = ++toastId;
      const item = {
        id,
        title: toast.title || '',
        message: toast.message || '',
        tone: toast.tone || 'info', // info | success | warn | error | gold
        duration: toast.duration ?? 4500,
        action: toast.action || null, // { label, onClick }
      };
      setToasts((prev) => [...prev.slice(-4), item]);
      if (item.duration > 0) {
        window.setTimeout(() => dismiss(id), item.duration);
      }
      return id;
    },
    [dismiss]
  );

  const value = useMemo(() => ({ push, dismiss }), [push, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed top-14 right-3 z-[2000] flex w-[min(22rem,calc(100vw-1.5rem))] flex-col gap-2 mdv-toast-host"
          aria-live="polite"
        >
          {toasts.map((t) => (
            <ToastCard key={t.id} toast={t} onDismiss={() => dismiss(t.id)} />
          ))}
        </div>,
        document.body
      )}
    </ToastContext.Provider>
  );
}

export function useToast() {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    return {
      push: () => null,
      dismiss: () => {},
    };
  }
  return ctx;
}

const toneStyles = {
  info: 'border-yellow-600/35 bg-zinc-950/95 text-zinc-100',
  success: 'border-emerald-500/40 bg-zinc-950/95 text-zinc-100',
  warn: 'border-amber-400/50 bg-zinc-950/95 text-zinc-100',
  error: 'border-red-500/50 bg-zinc-950/95 text-zinc-100',
  gold: 'border-yellow-500/60 bg-black/95 text-yellow-100',
};

function ToastCard({ toast, onDismiss }) {
  return (
    <div
      className={`pointer-events-auto overflow-hidden rounded-md border shadow-[0_8px_30px_rgba(0,0,0,0.45)] backdrop-blur-md ${
        toneStyles[toast.tone] || toneStyles.info
      }`}
      style={{ animation: 'mdvToastIn 220ms ease-out' }}
    >
      <div className="flex items-start gap-2 px-3 py-2.5">
        <div className="min-w-0 flex-1">
          {toast.title ? (
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-yellow-500/90">
              {toast.title}
            </p>
          ) : null}
          {toast.message ? (
            <p className={`text-[12px] leading-snug text-zinc-200 ${toast.title ? 'mt-0.5' : ''}`}>
              {toast.message}
            </p>
          ) : null}
          {toast.action ? (
            <button
              type="button"
              className="mt-1.5 text-[11px] font-semibold text-yellow-400 hover:text-yellow-300"
              onClick={() => {
                toast.action.onClick?.();
                onDismiss();
              }}
            >
              {toast.action.label}
            </button>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          className="shrink-0 text-zinc-500 hover:text-yellow-400 text-sm leading-none px-0.5"
          aria-label="Dismiss"
        >
          ×
        </button>
      </div>
    </div>
  );
}
